import { Injectable } from '@angular/core';
import { ISortOutput, SortDirection } from "libraries/interfaces/ISort";
import {
    IBookingCarbonFootprint, IJourneysRefAndAirRating,
    IQuote,
    IQuotes,
    ISegment
} from "libraries/service/resources/airTrip/IGetAirTripQuotes";
import { IAirRating } from "libraries/model/air-quote/IQuote";

export interface IJourneySelection {
    [quoteKey: string]: string[];
}

export enum EQuoteSorterActions {
    price = 'price',
    carbonFootprint = 'carbonFootprint',
    duration = 'duration',
    rating = 'rating',
}

@Injectable()
export class QuoteSorterService {

    private durations: { [key: string]: number };

    public sortQuotes(sortOutput: ISortOutput, quotes: IQuotes, journeySelection: IJourneySelection): IQuotes {
        return Object.entries(quotes)
            .sort(
                (a: [string, IQuote], b: [string, IQuote]) => {
                    let first: number;
                    let second: number;

                    const firstJourneySelection: string[] = journeySelection[a[0]];
                    const firstJourneySelectionString: string = [...(firstJourneySelection || [])].sort().toString();
                    const secondJourneySelection: string[] = journeySelection[b[0]];
                    const secondJourneySelectionString: string = [...(secondJourneySelection || [])].sort().toString();

                    switch (sortOutput.actionName) {
                        case EQuoteSorterActions.price:
                            first = a[1].total[0].value;
                            second = b[1].total[0].value;
                            break;

                        case EQuoteSorterActions.carbonFootprint:
                            first = this.extractCarbonFootprint(firstJourneySelectionString, a[1], sortOutput.direction);
                            second = this.extractCarbonFootprint(secondJourneySelectionString, b[1], sortOutput.direction);

                            break;

                        case EQuoteSorterActions.duration:
                            //temp calculation, should Be Done ni API like carbonFootprint
                            first = this.computeTotalDuration(firstJourneySelection, a[1]);
                            second = this.computeTotalDuration(secondJourneySelection, b[1]);
                            break;

                        case EQuoteSorterActions.rating:
                            first = this.extractRating(firstJourneySelectionString, a[1], sortOutput.direction);
                            second = this.extractRating(secondJourneySelectionString, b[1], sortOutput.direction);
                            break;

                        default:
                            return a[0].toString().localeCompare(b[0].toString());
                    }

                    return sortOutput.direction === SortDirection.ascending ?
                        first >= second ? +1 : -1 :
                        first >= second ? -1 : +1;
                }
            )
            .reduce(
                (accumulator: IQuotes, entry: [string, IQuote]) => {
                    return {
                        ...accumulator,
                        [entry[0]]: entry[1]
                    };
                },
                {});

    }

    private computeTotalDuration(journeySelection: string[], quote: IQuote): number {
        return (journeySelection || [])
            .map((id: string) => {
                    const relatedSegment: ISegment =
                        Object.values(quote.segment)
                            .find((segment: ISegment) => segment[id]);
                    return relatedSegment ? relatedSegment[id].duration.minutes : 0;
                }
            )
            .reduce((sum: number, minutes: number) => (sum + minutes), 0);
    }

    private extractCarbonFootprint(journeySelectionString: string, quote: IQuote, direction: SortDirection): number {

        //generate a value wich will always be the last
        const lastValue: number = direction === SortDirection.descending ?
            -1 :
            Number.MAX_VALUE;

        const relatedBookingCarbonFootPrint: IBookingCarbonFootprint = (quote.carbonFootprintArray || [])
            .find((carbonFootprintItem: IBookingCarbonFootprint): boolean =>
                carbonFootprintItem.journeyIds.sort().toString() === journeySelectionString
            );

        if (relatedBookingCarbonFootPrint?.carbonFootprint) {
            const isUnknown: boolean = relatedBookingCarbonFootPrint.carbonFootprint.xStatus &&
                relatedBookingCarbonFootPrint.carbonFootprint.xStatus === 'Unknown';
            // quotes with carbonFootprint.status Unknown are in the end
            return isUnknown ?
                lastValue :
                relatedBookingCarbonFootPrint.carbonFootprint.value;
        }
        // quotes with no CFP are in the end
        return lastValue;
    }

    private extractRating(journeySelectionString: string, quote: IQuote, direction: SortDirection): number {

        const lastValue: number = direction === SortDirection.descending ?
            -1 :
            20;

        const relatedBookingRating: IJourneysRefAndAirRating = quote.ratingArray
            .find((ratingArray: IJourneysRefAndAirRating) =>
                ratingArray.journeyIds.sort().toString() === journeySelectionString
            );

        if (relatedBookingRating && relatedBookingRating.rating) {
            return relatedBookingRating.rating.value;
        }

        return lastValue;
    }
}
